import {makeAutoObservable, action, runInAction} from "mobx";
import { getUserHistory, deleteTask } from "../utils/serverRequest";
import { TrainModelParam } from "../model/TrainModelParam";
import { startTrainModel, fetchTaskProgress, getTaskInfo } from "../utils/serverRequest";
import { delay } from "../utils/misc";
import { MSG_TYPE, msgbox } from "../utils/Msgbox";
import * as ClientZip from "client-zip"

export enum ASSET_TYPE{
    NONE,
    IMAGE,
    MESH,
    MTL,
    ALBEDO,
    CRM,
    CCM,
    KPS,
    AUDIO,
    MASK,
    VIDEO = 12,
    SAFE_TENSOR
}

export enum TASK_STATUS{
    NONE,
    WAITING,
    PROCESSING,
    SUCCESS,
    FAILED,
    CREATE_TASK_FAILED,
    NSFW
}

const TASK_TYPE: number = 91;
const RESOURCE_PREFIX: string = "https://assets.hanchaomr.com/YunmoGenAssets";

// 训练模型
export class TrainInfoModel{
    cover: string = ""; // 封面
    name: string = ""; // 名字
    size: number = 0; // 大小
    id: string = "";
    trigger: string = ""; //触发词
    modelUrl: string = ""; // 模型文件链接

    constructor(){
        makeAutoObservable(this);
    }

    static fromJson(a: any): TrainInfoModel{
        const res = new TrainInfoModel();
        res.name = a.file.substring(a.file.lastIndexOf("/") + 1, a.file.lastIndexOf("."));
        res.id = a.id;
        res.modelUrl = `${RESOURCE_PREFIX}${a.file}`;
        return res;
    }
}

// 训练记录
export class TrainInfo{
    name: string = "";
    createTime: string = "";
    models: TrainInfoModel[] = [];
    size: number = 0;
    id: string = "";
    status: TASK_STATUS = TASK_STATUS.NONE;

    constructor(){
        makeAutoObservable(this);
    }

    static fromJson(a: any): TrainInfo{
        const res = new TrainInfo();
        res.id = a.id;
        res.name = a.id;
        res.createTime = a.createTime;
        res.status = Number(a.status);

        let cover = "";
        a.assets.forEach((item: any) => {
            switch(Number(item.type)){
                case ASSET_TYPE.IMAGE:
                    cover = `${RESOURCE_PREFIX}${item.file}`;
                    break;
                case ASSET_TYPE.SAFE_TENSOR:
                    res.models = [TrainInfoModel.fromJson(item)];
                    break;
            }
        });

        if(res.models.length > 0 && cover.length > 0){
            res.models[0].cover = cover;
        }
        return res;
    }

    static arrayFromJson(a: any): TrainInfo[]{
        return a.filter((item: any) => Number(item.type) === TASK_TYPE).map((res: any) => {
            return TrainInfo.fromJson(res);
        });
    }
}

export class TrainManageStore{
    constructor(){
        makeAutoObservable(this);
        this.hintOnRefresh();
    }

    private hintOnRefresh = (): void => {
        window.addEventListener("beforeunload", (event) => {
            if (this.status !== TASK_STATUS.NONE) {
                event.preventDefault();
            }

            return "";
        });
    };

    private lastTimestamp: number = 0;
    private timer: any = null;
    
    private _history: TrainInfo[] = [];
    get history(): TrainInfo[]{
        return this._history;
    }

    // 训练进度
    private _progress: number = 0;
    get progress(): number{
        return this._progress;
    }
    
    // 任务状态
    private _status: TASK_STATUS = TASK_STATUS.NONE;
    get status(): TASK_STATUS{
        return this._status;
    }

    // 总时间
    private _totalTime: number = 0;
    get totalTime(): number{
        return this._totalTime;
    }
    set totalTime(a: number){
        this._totalTime = Math.max(5 * 60, a);
    }

    // 经过时间
    private _timeElapsed: number = 0;
    get timeElapsed(): number{
        return this._timeElapsed;
    }

    interupt(){
        this._status = TASK_STATUS.NONE;
        this.stopTick();
    }

    @action
    init(): Promise<void>{
        return getUserHistory(TASK_TYPE)
            .then(res => {
                this._history = TrainInfo.arrayFromJson(res);
                
                const waitingTask = this._history.find(h => h.status < TASK_STATUS.SUCCESS);
                if(!!waitingTask && this._status  === TASK_STATUS.NONE){
                    this._status = TASK_STATUS.WAITING;
                    this._timeElapsed = 0;
                    this.tick();

                    this.updateProgress(waitingTask.id)
                        .then(getTaskInfo)
                        .then(res => this._history.push(TrainInfo.fromJson(res)))
                        .catch(reason => {
                            if(this._status === TASK_STATUS.NONE){
                                return;
                            }
                            msgbox(reason.message, MSG_TYPE.ERROR);
                        })
                        .finally(() => this._status = TASK_STATUS.NONE);
                }
                return Promise.resolve();
            });
    }

    @action
    deleteModel(ids: string[]): Promise<void>{
        return Promise.all(ids.map(deleteTask)).then(() => {
            const _ids = new Set<string>(ids);
            this._history = this.history.filter(h => !_ids.has(h.id));
            return Promise.resolve();
        });
    }

    @action
    addTrainInfo(info: TrainInfo): void{
        this._history = [info].concat(this._history);
    }

    @action
    private updateProgress(taskId: string): Promise<string>{
        return fetchTaskProgress(taskId).then(res => {
            if(!res){
                return delay(3000).then(() => this.updateProgress(taskId));
            }

            const status = Number(res.status);
            if(status === TASK_STATUS.FAILED || status === TASK_STATUS.NSFW){
                this.stopTick();
                return Promise.reject(new Error("训练失败"));
            }

            const newTimestamp = Date.now();
            const newProgress = Math.min(0.99, Math.max(this._progress, res.progress || 0));
            if(newProgress > this._progress && newTimestamp > this.lastTimestamp){
                this.lastTimestamp = newTimestamp;
                this.totalTime = Math.floor(1 / (newProgress - this._progress) * (newTimestamp - this.lastTimestamp) / 1000);
            }

            runInAction(() => this._progress = newProgress);

            if(status !== TASK_STATUS.SUCCESS && status !== TASK_STATUS.NSFW){
                this._status = status;
                return delay(status === TASK_STATUS.WAITING ? 3000 : 1000).then(() => this.updateProgress(taskId));
            }

            this.stopTick();
            return Promise.resolve(taskId);
        });
    };

    private stopTick(): void{
        clearTimeout(this.timer);
        this.timer = null;
    }

    private tick(): void {
        this._timeElapsed += 1;
        this.timer = setTimeout(() => this.tick(), 1000);
    };
    
    @action
    startTrain(param: TrainModelParam): Promise<any>{
        this._progress = 0;
        this._timeElapsed = 0;
        this._status = TASK_STATUS.WAITING;
        this.tick();

        return startTrainModel(param)
            .then(taskId => this.updateProgress(taskId))
            .then(taskId => getTaskInfo(taskId))
            .then(res => this._history.push(TrainInfo.fromJson(res)))
            .finally(() => this._status = TASK_STATUS.NONE);
    }

    downloadModel(ids: string[]): Promise<void>{
        if(ids.length <= 0){
            return Promise.resolve();
        }

        const _ids = new Set<string>(ids);
        const models = this._history.filter(h => _ids.has(h.id)).map(h => h.models[0]);
        if(models.length <= 0){
            return Promise.resolve();
        }

        // return Promise.all(models.map(model => {
        //     return fetch(model.modelUrl).then(res => {
        //         return fetch(model.cover).then(coverRes => 
        //             Promise.resolve([
        //                 {input: res, name: model.modelUrl.substring(model.modelUrl.lastIndexOf("/") + 1)},
        //                 {input: coverRes, name: model.cover.substring(model.cover.lastIndexOf("/") + 1)}
        //             ])
        //         );
        //     });
        // })).then(res => {
        //     return ClientZip.downloadZip(res.flat()).blob();
        // }).then(b => {
        //     const a = document.createElement("a");
        //     a.href = URL.createObjectURL(b);
        //     a.download = `${Date.now()}.zip`;
        //     a.click();
        // });
        const a = document.createElement("a");
        a.href = models[0].modelUrl;
        a.click();
        return Promise.resolve();
    }
}